<script>
import { Line } from "vue-chartjs";
import "@/plugins/chart";

export default {
    name: "LineChart",
    extends: Line,
    props: {
        title: {
            type: String,
            default: null,
        },
        labels: {
            type: Array,
            default: () => [],
        },
        datasets: {
            type: Array,
            default: () => [],
        },
        minY: {
            type: [String, Number],
            default: undefined,
        },
        maxY: {
            type: [String, Number],
            default: undefined,
        },
        stepY: {
            type: [String, Number],
            default: undefined,
        },
        paddingTop: {
            type: [String, Number],
            default: 0,
        },
        offsetX: {
            type: Boolean,
            default: false,
        },
        zones: {
            type: Array,
            default: () => [],
        },
        valueFormatter: {
            type: Function,
            default: null,
        },
    },
    computed: {
        data(vm = this) {
            return {
                labels: vm.labels,
                datasets: vm.datasets,
            }
        },
        options(vm = this) {
            const theme = vm.$vuetify.theme.currentTheme;
            const stepY = vm.stepY != null ? Number(vm.stepY) : undefined;
            const beginAtZero = vm.minY == null || Number(vm.minY) === 0;
            return {
                responsive: true,
                maintainAspectRatio: false,
                scales: {
                    yAxes: [
                        {
                            id: 'left-axis',
                            type: 'linear',
                            position: 'left',
                            ticks: {
                                beginAtZero: beginAtZero,
                                min: vm.minY,
                                max: vm.maxY,
                                stepSize: stepY,
                                precision: stepY != null ? 0 : undefined,
                                ...(vm.valueFormatter ? { callback: value => vm.valueFormatter(value) } : {}),
                            },
                        },
                        ...(vm.hasRightAxis
                            ? [{
                                id: 'right-axis',
                                type: 'linear',
                                position: 'right',
                                ticks: {
                                    beginAtZero: true,
                                    min: vm.minY,
                                    max: vm.maxY,
                                },
                                gridLines: {
                                    display: false,
                                },
                            }]
                            : [])
                    ],
                    xAxes: [{
                        offset: vm.offsetX,
                        gridLines: {
                            display: false
                        },
                    }],
                },
                legend: {
                    display: false,
                },
                title: {
                    display: !!vm.title,
                    text: vm.title
                },
                layout: {
                    padding: {
                        top: vm.paddingTop || 0,
                    }
                },
                ...(vm.hasManyDatasets ? { hover: { mode: 'index', intersect: false, axis: 'x' } } : {}),
                tooltips: {
                    mode: vm.hasManyDatasets ? 'index' : 'nearest',
                    intersect: !vm.hasManyDatasets,
                    axis: 'x',
                    backgroundColor: theme.surface2,
                    titleFontColor: theme.txtPrimary,
                    bodyFontColor: theme.txtSecondary,
                    borderColor: theme.outline,
                    borderWidth: 1,
                    cornerRadius: 8,
                    xPadding: 10,
                    yPadding: 8,
                    titleFontFamily: '"Outfit", sans-serif',
                    bodyFontFamily: '"Outfit", sans-serif',
                    callbacks: {
                        ...(vm.valueFormatter ? {
                            label: function (tooltipItem, data) {
                                const dataset = data.datasets[tooltipItem.datasetIndex] || {}
                                const value = vm.valueFormatter(tooltipItem.yLabel)
                                return dataset.label ? `${dataset.label}: ${value}` : `${value}`
                            },
                        } : {}),
                        title: function (tooltipItems, data) {
                            const tooltipItem = tooltipItems[0];
                            const dataset = data.datasets[tooltipItem.datasetIndex];
                            if (dataset.dates && dataset.dates[tooltipItem.index]) return dataset.dates[tooltipItem.index];
                            const point = dataset.data[tooltipItem.index];
                            if (point && point.x) return point.x;
                            if (point && point.dateString) return point.dateString;
                            return data.labels[tooltipItem.index] || '';
                        },
                        afterBody: function (tooltipItems, data) {
                            const lines = [];
                            tooltipItems.forEach(tooltipItem => {
                                const dataset = data.datasets[tooltipItem.datasetIndex];
                                const details = dataset.details ? dataset.details[tooltipItem.index] : null;
                                if (!details || !details.length) return;
                                details.forEach(detail => lines.push(`· ${detail}`));
                            });
                            return lines;
                        },
                    }
                }
            }
        },
        hasRightAxis(vm = this) {
            return vm.datasets.some(dataset => dataset.yAxisID === 'right-axis')
        },
        hasManyDatasets(vm = this) {
            return vm.datasets.length > 1
        }
    },
    mounted() {
        const vm = this
        vm.addPlugin({
            id: 'lineChartZones',
            beforeDatasetsDraw(chart) {
                const zones = vm.zones || []
                if (!zones.length) return
                const axis = chart.scales['left-axis']
                const area = chart.chartArea
                if (!axis || !area) return

                const ctx = chart.ctx
                ctx.save()
                zones.forEach(zone => {
                    const from = zone.from == null ? axis.min : Math.max(zone.from, axis.min)
                    const to = zone.to == null ? axis.max : Math.min(zone.to, axis.max)
                    if (to <= from) return

                    const top = axis.getPixelForValue(to)
                    const bottom = axis.getPixelForValue(from)

                    if (zone.color) {
                        ctx.fillStyle = zone.color
                        ctx.fillRect(area.left, top, area.right - area.left, bottom - top)
                    }
                    if (zone.line && from > axis.min) {
                        ctx.beginPath()
                        ctx.setLineDash([4, 4])
                        ctx.lineWidth = 1
                        ctx.strokeStyle = zone.line
                        ctx.moveTo(area.left, bottom)
                        ctx.lineTo(area.right, bottom)
                        ctx.stroke()
                        ctx.setLineDash([])
                    }
                })
                ctx.restore()
            }
        })
        vm.renderChart(vm.data, vm.options);
    },
    watch: {
        data() {
            const vm = this
            vm.renderChart(vm.data, vm.options);
        },
        options() {
            const vm = this
            vm.renderChart(vm.data, vm.options);
        },
        zones() {
            const vm = this
            vm.renderChart(vm.data, vm.options);
        }
    }
};
</script>